import { cleanseDataBeforeSave } from './cleanseDataBeforeSave';
import { mapToAlertExternalRecipientsChunk, mapToAlertInternalRecipients } from './mapToAlertRecipients';
export const PLACEHOLDER_FOR_SENT_DATE = '${sentDate}';
// this mapping must remain explicit, do not use spread operator, because formData is different object and can contain differently shaped properties
export function mapToApiSaveAlertRequest({ alertData, userId, secureMessageHint, genericMessage, phinHtmlMessageOptions, stateCode, appTitle, useConsentlessTexting, alertOwners, footerText, }) {
    var _a, _b, _c, _d, _e, _f, _g;
    const alertType = alertData.type || 'draft';
    const additionalData = (alertData === null || alertData === void 0 ? void 0 : alertData.additionalData) ? alertData.additionalData : {};
    const request = {
        title: alertData.title,
        ownerId: alertData.ownerId,
        messageType: alertData.messageType,
        subject: alertData.subject,
        textMessage: alertData.textMessage,
        responseOptions: alertData.allowResponse ? transformToApiResponseOption(alertData.responseOptions) : [],
        channels: alertData.channels,
        type: alertType,
        deliveryMode: alertData.deliveryOrder.deliveryMode,
        htmlMessage: alertData.htmlMessage,
        settings: mapSettings(alertData),
        recipients: {
            groups: [],
            contacts: [],
        },
        sendDateTime: alertData.sendDateTime ? new Date(alertData.sendDateTime).toISOString() : undefined,
        additionalData: Object.assign(Object.assign(Object.assign(Object.assign({}, additionalData), { recipients: JSON.stringify(alertData.recipients) }), (alertData.customAlertData ? { customAlertData: JSON.stringify(alertData.customAlertData) } : {})), (alertData.followUp ? { followUp: JSON.stringify(alertData.followUp) } : {})),
        emailCc: alertData.emailCc,
        icon: alertData.icon,
        voiceMessage: alertData.voiceMessage,
        voiceMessageType: !alertData.channels.includes('voice') ? undefined : alertData.voiceMessageType,
        voiceRecordings: !alertData.channels.includes('voice') && alertData.voiceMessageType !== 'textToVoice' ? undefined : alertData.voiceRecordings,
        color: alertData.color,
        permissions: alertData.permissions ? alertData.permissions : undefined,
        phin: alertData.phin === true
            ? transformToApiPhinOptions(alertData.phin, alertData.phinOptions, alertData.subject, alertData.messageType, phinHtmlMessageOptions, stateCode, appTitle)
            : undefined,
        validityInHours: alertData.validityInHours,
        secure: alertData.phin ? (_a = alertData.phinOptions) === null || _a === void 0 ? void 0 : _a.communicationSensitive : undefined,
        secureMessageHint,
        genericMessage,
        consentless: useConsentlessTexting,
        searchData: alertData.searchData,
        messageFooter: {
            voice: (_b = footerText === null || footerText === void 0 ? void 0 : footerText.voice) === null || _b === void 0 ? void 0 : _b.replace('<appTitle>', appTitle !== null && appTitle !== void 0 ? appTitle : 'Application'),
            sms: (_c = footerText === null || footerText === void 0 ? void 0 : footerText.sms) === null || _c === void 0 ? void 0 : _c.replace('<appTitle>', appTitle !== null && appTitle !== void 0 ? appTitle : 'Application'),
            email: (_d = footerText === null || footerText === void 0 ? void 0 : footerText.email) === null || _d === void 0 ? void 0 : _d.replace('<appTitle>', appTitle !== null && appTitle !== void 0 ? appTitle : 'Application'),
            inbox: (_e = footerText === null || footerText === void 0 ? void 0 : footerText.inbox) === null || _e === void 0 ? void 0 : _e.replace('<appTitle>', appTitle !== null && appTitle !== void 0 ? appTitle : 'Application'),
            fax: (_f = footerText === null || footerText === void 0 ? void 0 : footerText.fax) === null || _f === void 0 ? void 0 : _f.replace('<appTitle>', appTitle !== null && appTitle !== void 0 ? appTitle : 'Application'),
            pager: (_g = footerText === null || footerText === void 0 ? void 0 : footerText.pager) === null || _g === void 0 ? void 0 : _g.replace('<appTitle>', appTitle !== null && appTitle !== void 0 ? appTitle : 'Application'),
        },
    };
    return cleanseDataBeforeSave(request, userId, alertData.emailAttachments, alertOwners);
}
function mapSettings(formData) {
    var _a, _b;
    const escalationDelay = [];
    if (formData.deliveryOrder.voiceDelay !== undefined) {
        escalationDelay.push({ channel: 'voice', delay: formData.deliveryOrder.voiceDelay });
    }
    if (formData.deliveryOrder.textDelay !== undefined) {
        escalationDelay.push({ channel: 'sms', delay: formData.deliveryOrder.textDelay });
    }
    return {
        escalationDelay: escalationDelay,
        voiceRepeatCount: (_a = formData.voiceOptions) === null || _a === void 0 ? void 0 : _a.repeatTimes,
        voiceRepeatDelay: (_b = formData.voiceOptions) === null || _b === void 0 ? void 0 : _b.waitTime,
    };
}
export function getDeliveryTimeInMinutesLabel(deliveryTimeInMinutes) {
    const hours = Math.floor(deliveryTimeInMinutes / 60);
    const minutes = deliveryTimeInMinutes % 60;
    return ((hours > 0 ? hours + (hours > 1 ? ' hours ' : ' hour ') : '') +
        (minutes > 0 ? minutes + (minutes > 1 ? ' minutes' : ' minute') : ''));
}
export function getAlertInternalRecipients({ internalRecipients, alertChannels, config, context, userFilters, }) {
    return mapToAlertInternalRecipients({ context, internalRecipients, alertChannels, userFilters, config });
}
export function getAlertExternalRecipientsChunk(importId, alertChannels, config, context, limit, offset) {
    return mapToAlertExternalRecipientsChunk(context, importId, alertChannels, limit, offset);
}
function transformToApiResponseOption(responseOptions) {
    return responseOptions
        ? responseOptions.map((option, index) => ({
            optionId: (index + 1).toString(),
            text: option.text,
            conferenceBridgeMeetingId: (option === null || option === void 0 ? void 0 : option.conferenceBridgeMeetingId)
                ? option === null || option === void 0 ? void 0 : option.conferenceBridgeMeetingId.replace(/\s/g, '')
                : undefined,
            conferenceBridgeNumber: option === null || option === void 0 ? void 0 : option.conferenceBridgeNumber,
        }))
        : [];
}
function getPhinDataHtml(params) {
    var _a, _b, _c;
    return `<p><strong>Sensitive</strong>: ${params.phin.communicationSensitive ? 'Yes' : 'No'}</p>
<p><strong>Alert Title</strong>: ${params.subject || ''}</p>
<p><strong>Agency Name</strong>: ${(params === null || params === void 0 ? void 0 : params.appTitle) ? params.appTitle : ''}</p>
<p><strong>Agency Abbreviation</strong>: ${(params === null || params === void 0 ? void 0 : params.stateCode) ? params.stateCode : ''}</p>
<p><strong>Alert Program</strong>: ${(params === null || params === void 0 ? void 0 : params.phinHtmlMessageOptions) && ((_a = params.phinHtmlMessageOptions) === null || _a === void 0 ? void 0 : _a.product) ? params.phinHtmlMessageOptions.product : ''}<br></p>
<p><strong>Unique Identifier</strong>: ${params.phin.uniqueIdentifier || ''}</p>
<p><strong>PHIN Message Type</strong>: ${params.phin.messageType || ''}</p>
<p><strong>Urgency of Event</strong>: ${params.phin.urgencyOfEvent || ''}</p>
<p><strong>Severity of Event</strong>: ${params.phin.severityOfEvent || ''}</p>
<p><strong>Certainty of Event</strong>: ${params.phin.certaintyOfEvent || ''}</p>
<p><strong>Delivery Time</strong>: ${getDeliveryTimeInMinutesLabel(params.phin.deliveryTimeInMinutes || 0)}</p>
<p><strong>Acknowledge this Message</strong>: ${params.phin.recipientAcknowledge ? 'Yes' : 'No'}</p>
<p><strong>Send Time</strong>: ${PLACEHOLDER_FOR_SENT_DATE}</p>
<p><strong>Status</strong>: ${params.messageType || ''}</p>
<p><strong>Reference Alert:</strong></p>
<p><strong>Jurisdiction</strong>: 0</p>
<p><strong>Jurisdiction Level</strong>: ${(params === null || params === void 0 ? void 0 : params.phinHtmlMessageOptions) && ((_b = params.phinHtmlMessageOptions) === null || _b === void 0 ? void 0 : _b.jurisdictionLevel)
        ? params.phinHtmlMessageOptions.jurisdictionLevel
        : ''}</p>
<p><strong>Role</strong>: ${(params === null || params === void 0 ? void 0 : params.phinHtmlMessageOptions) && ((_c = params.phinHtmlMessageOptions) === null || _c === void 0 ? void 0 : _c.productRole) ? params.phinHtmlMessageOptions.productRole : ''}</p>`;
}
function transformToApiPhinOptions(phin, phinOptions, subject, messageType, phinHtmlMessageOptions, stateCode, appTitle) {
    return phin && phinOptions !== undefined
        ? Object.assign(Object.assign({}, phinOptions), { htmlMessage: getPhinDataHtml({
                phin: phinOptions,
                subject,
                messageType,
                phinHtmlMessageOptions,
                stateCode,
                appTitle,
            }) }) : undefined;
}
